/**
 * Doctors.jsx - Featured doctors showcase on homepage
 * 
 * Displays a filterable grid of featured doctors from each specialty:
 * - Orthopedic Surgeon
 * - Oncologist
 * - Dental Specialist
 * - Cosmetic Surgeon
 * 
 * Each doctor card shows:
 * - Photo
 * - Name and specialty
 * - Qualifications
 * - Experience
 * - Hospital affiliation
 * - Specialization areas
 * 
 * @author Curis Path Development Team
 */

import React, { useState } from 'react';
import { doctors } from '../data/mock';
import { GraduationCap, Briefcase, Building2, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Doctors = () => {
  const navigate = useNavigate();
  
  // Filter state - defaults to showing all specialties
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');
  
  // Available specialty filters (must match doctor.specialty values in mock.js)
  const specialties = ['All', 'Orthopedic Surgeon', 'Oncologist', 'Dental Specialist', 'Cosmetic Surgeon'];
  
  // Get only featured doctors for homepage display
  const featuredDoctors = doctors.filter(doc => doc.featured);
  
  // Apply specialty filter
  const filteredDoctors = selectedSpecialty === 'All' 
    ? featuredDoctors 
    : featuredDoctors.filter(doc => doc.specialty === selectedSpecialty);

  /**
   * Scrolls to the enquiry form section
   */
  const scrollToEnquiry = () => {
    const element = document.getElementById('enquiry');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Color classes for card backgrounds (cycles through for variety)
  const colorClasses = ['accent-blue', 'accent-purple', 'accent-orange', 'accent-pink', 'accent-green'];
  
  return (
    <section 
      id="doctors" 
      className="section-padding" 
      style={{ background: 'var(--bg-section)' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Meet Our Expert Doctors</h2>
          <p 
            className="body-large mx-auto" 
            style={{ maxWidth: '700px', color: 'var(--text-secondary)' }}
          >
            Internationally trained specialists with decades of combined experience
          </p>
        </div>

        {/* Specialty Filter Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <div 
            className="flex items-center gap-2 body-small" 
            style={{ color: 'var(--text-primary)' }}
          >
            <Filter size={18} />
            <span className="font-semibold">Filter by Specialty:</span>
          </div>
          
          {specialties.map((specialty) => (
            <button
              key={specialty}
              onClick={() => setSelectedSpecialty(specialty)}
              className="caption px-4 py-2 rounded-full transition-all"
              style={{
                background: selectedSpecialty === specialty 
                  ? 'var(--text-primary)' 
                  : 'rgba(255, 255, 255, 0.6)',
                color: selectedSpecialty === specialty 
                  ? 'white' 
                  : 'var(--text-primary)',
                border: `1px solid ${selectedSpecialty === specialty 
                  ? 'var(--text-primary)' 
                  : 'rgba(0, 0, 0, 0.1)'}`,
                cursor: 'pointer',
                fontWeight: selectedSpecialty === specialty ? '600' : '400'
              }}
            >
              {specialty}
            </button>
          ))}
        </div>

        {/* Doctors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDoctors.map((doctor, index) => (
            <div 
              key={doctor.id} 
              className={`voice-card ${colorClasses[index % colorClasses.length]}`}
            >
              {/* Doctor Photo */}
              <div 
                className="mb-4 rounded-lg overflow-hidden" 
                style={{ height: '280px' }}
              >
                <img 
                  src={doctor.image} 
                  alt={doctor.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Doctor Name & Specialty */}
              <h3 className="heading-3 mb-1">{doctor.name}</h3>
              <p 
                className="body-small font-semibold mb-3" 
                style={{ color: 'var(--text-primary)' }}
              >
                {doctor.specialty}
              </p>

              {/* Doctor Details */}
              <div className="space-y-2 mb-4">
                {/* Qualifications */}
                <div className="flex items-start gap-2">
                  <GraduationCap 
                    size={18} 
                    style={{ 
                      color: 'var(--text-muted)', 
                      marginTop: '2px', 
                      flexShrink: 0 
                    }} 
                  />
                  <span 
                    className="body-small" 
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {doctor.qualification}
                  </span>
                </div>

                {/* Experience */}
                <div className="flex items-center gap-2">
                  <Briefcase 
                    size={18} 
                    style={{ color: 'var(--text-muted)', flexShrink: 0 }} 
                  />
                  <span 
                    className="body-small" 
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {doctor.experience} Experience
                  </span>
                </div>

                {/* Hospital */}
                <div className="flex items-center gap-2">
                  <Building2 
                    size={18} 
                    style={{ color: 'var(--text-muted)', flexShrink: 0 }} 
                  />
                  <span 
                    className="body-small" 
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {doctor.hospital}
                  </span>
                </div>

                {/* Specialization (if available) */}
                {doctor.specialization && (
                  <div 
                    className="mt-3 pt-3" 
                    style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}
                  >
                    <p 
                      className="caption font-semibold mb-1" 
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Specialization:
                    </p>
                    <p 
                      className="body-small" 
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {doctor.specialization}
                    </p>
                  </div>
                )}
              </div>

              {/* Book Consultation CTA */}
              <button 
                className="btn-secondary w-full"
                onClick={scrollToEnquiry}
              >
                Book Consultation
              </button>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredDoctors.length === 0 && (
          <div className="text-center py-12">
            <p 
              className="body-large" 
              style={{ color: 'var(--text-secondary)' }}
            >
              No doctors found for the selected specialty.
            </p>
          </div>
        )}

        {/* View All Doctors Link */}
        <div className="text-center mt-8">
          <button 
            onClick={() => navigate('/doctors')}
            className="btn-secondary"
          >
            View All Doctors
          </button>
        </div>
      </div>
    </section>
  );
};

export default Doctors;
